import { Components, createTheme, lighten, Theme } from '@mui/material/styles'
import { ThemeConfigObject, ThemeConfigOverrides } from './types'
import variables from './variables'

declare module '@mui/material/styles' {
  interface Theme {
    variables: ThemeConfigObject
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    variables?: ThemeConfigObject
    overrides?: ThemeConfigOverrides
    components?: Components
  }
}

export const getTheme = (): Theme => {
  const { colors, fonts, fontWeights } = variables
  const {
    bottomBarMain,
    buttonBasic,
    inputField,
    inputLabel,
    primaryMain,
    secondaryMain,
    tableCell,
    tabNavigation
  } = colors
  const buttonActiveStyle = {
    borderColor: buttonBasic,
    backgroundColor: lighten(buttonBasic, 0.9)
  }

  const fieldBorderColor = inputField.border
  const fieldBorderStyleNarrow = `1px solid ${fieldBorderColor}`
  const fieldBorderStyleWide = `2px solid ${fieldBorderColor}`

  const fieldLabelColor = inputLabel.color

  const tabLinkColor = tabNavigation.tabLink.color
  const tabLinkActiveStyle = {
    color: tabLinkColor,
    borderBottom: `3px solid ${tabNavigation.tabLinkActive.color}`
  }

  return createTheme({
    variables: variables,
    palette: {
      mode: undefined,
      bottomBar: {
        main: bottomBarMain
      },
      primary: {
        main: primaryMain
      },
      secondary: {
        main: secondaryMain
      }
    },
    typography: {
      fontFamily: fonts.default
    },
    overrides: {
      MuiDrawer: {
        paper: {
          width: '80%'
        }
      },
      MuiInputLabel: {
        root: {
          color: '#666',
          '&$focused': {
            color: '#333'
          }
        }
      }
    },
    components: {
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:before': {
              borderBottom: fieldBorderStyleNarrow
            },
            '&:after': {
              borderBottom: fieldBorderStyleWide
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: fieldLabelColor,
            '&.Mui-focused': {
              color: fieldLabelColor
            }
          }
        }
      },
      MuiLink: {
        styleOverrides: {
          root: {
            '& p': {
              fontWeight: fontWeights.buttonFont
            }
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& fieldset': {
              border: fieldBorderStyleNarrow
            },
            '&:hover fieldset': {
              border: fieldBorderStyleWide
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              border: fieldBorderStyleNarrow
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            color: buttonBasic,
            borderColor: buttonBasic,
            fontWeight: fontWeights.buttonFont,
            '&:active': buttonActiveStyle,
            '&:hover': buttonActiveStyle
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            borderBottom: `5px solid ${primaryMain}`,
            color: tabLinkColor,
            fontFamily: fonts.default,
            fontSize: '16px',
            fontWeight: 400,
            padding: 0,
            margin: '0 80px 0 0',
            minHeight: 0,
            minWidth: 0,
            '&.Mui-selected': tabLinkActiveStyle,
            '&:hover': tabLinkActiveStyle,
            '&:hover.Mui-selected ': tabLinkActiveStyle
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: primaryMain
          },
          root: {
            margin: '28px 0 0 0',
            minHeight: '28px'
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            paddingLeft: 0
          },
          head: tableCell.head,
          body: {
            fontSize: 14
          }
        }
      }
    }
  })
}
